import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { MapPin, ShieldCheck, ArrowRight, Phone, Star } from 'lucide-react';
import Header from '../components/Header';
import { api } from '../lib/api';

// Slug helpers
const slugify = (s) => (s || '').toLowerCase().trim().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
const unslug = (s) => (s || '').split('-').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

export default function SuburbLanding() {
  const { category, suburb } = useParams();
  const [tradies, setTradies] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const suburbName = unslug(suburb);
  const catObj = categories.find((c) => c.id === category);
  const catName = catObj ? catObj.name : unslug(category);

  useEffect(() => {
    api.categories().then((r) => setCategories(r.data));
    api.listTrades({ category, suburb: suburbName }).then((r) => {
      setTradies(r.data);
      setLoading(false);
    });
  }, [category, suburb]);  // eslint-disable-line

  // SEO: set document title + meta + schema.org JSON-LD
  useEffect(() => {
    const title = `${catName}s in ${suburbName}, Perth — verified tradies · Real Tradies`;
    const desc = `Find hand-verified ${catName.toLowerCase()}s serving ${suburbName}, Perth. Live compliance status, real reviews, direct phone numbers. Free forever for tradies. Real Tradies · Real Neighbours.`;
    document.title = title;
    const setMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    setMeta('description', desc);

    // JSON-LD structured data
    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: `${catName}s in ${suburbName}, Perth`,
      itemListElement: tradies.slice(0, 20).map((t, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        item: {
          '@type': 'LocalBusiness',
          name: t.business_name,
          address: { '@type': 'PostalAddress', addressLocality: t.suburb, addressRegion: 'WA', addressCountry: 'AU' },
          aggregateRating: t.review_count > 0 ? { '@type': 'AggregateRating', ratingValue: t.avg_rating, reviewCount: t.review_count } : undefined,
          telephone: t.phone,
        },
      })),
    };
    let script = document.getElementById('realTradies-suburb-jsonld');
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = 'realTradies-suburb-jsonld';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(jsonLd);

    return () => {
      const s = document.getElementById('realTradies-suburb-jsonld');
      if (s) s.remove();
    };
  }, [tradies, catName, suburbName]);

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="suburb-landing">
      <Header />

      {/* Hero */}
      <section className="border-b border-[#E5E4E0] bg-white">
        <div className="mx-auto max-w-7xl px-6 py-14 md:px-10 md:py-20">
          <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#D96C4E]">Perth · WA · {suburbName}</div>
          <h1 className="mt-6 font-heading text-4xl font-bold leading-[1.05] tracking-tight text-[#1C1B1A] md:text-6xl lg:text-7xl">
            Verified <span className="italic text-[#1A362D]">{catName.toLowerCase()}s</span>
            <br />in {suburbName}
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-[#5C5B5A]">
            Every {catName.toLowerCase()} listed here has been hand-verified by Real Tradies — license, insurance,
            workers compensation, ABN. No paid rankings. No lead auctions. Call them directly.
          </p>
          <div className="mt-6 flex flex-wrap gap-3 text-xs font-semibold uppercase tracking-[0.15em] text-[#1A362D]">
            <span className="inline-flex items-center gap-1 rounded-full border border-[#1A362D] bg-[#1A362D]/[0.05] px-3 py-1.5"><ShieldCheck size={12} /> Hand-verified</span>
            <span className="inline-flex items-center gap-1 rounded-full border border-[#1A362D] bg-[#1A362D]/[0.05] px-3 py-1.5">Free to contact</span>
            <span className="inline-flex items-center gap-1 rounded-full border border-[#1A362D] bg-[#1A362D]/[0.05] px-3 py-1.5">Live compliance</span>
          </div>
        </div>
      </section>

      {/* Results */}
      <section>
        <div className="mx-auto max-w-7xl px-6 py-12 md:px-10">
          {loading ? (
            <div className="text-sm text-[#8A8988]">Finding {catName.toLowerCase()}s in {suburbName}...</div>
          ) : tradies.length === 0 ? (
            <div className="border border-dashed border-[#E5E4E0] bg-white p-8">
              <div className="font-heading text-2xl font-bold text-[#1C1B1A]">
                No verified {catName.toLowerCase()}s in {suburbName} yet.
              </div>
              <p className="mt-2 text-[#5C5B5A]">
                We're growing fast — try browsing all Perth {catName.toLowerCase()}s or checking a neighbouring suburb.
              </p>
              <button onClick={() => navigate(`/search?category=${category}`)} className="btn-primary mt-4">
                Browse all {catName.toLowerCase()}s in Perth <ArrowRight size={14} />
              </button>
            </div>
          ) : (
            <>
              <div className="mb-6 flex items-end justify-between">
                <div className="font-heading text-2xl font-bold text-[#1C1B1A]" data-testid="suburb-result-count">
                  {tradies.length} verified {catName.toLowerCase()}{tradies.length !== 1 ? 's' : ''}
                </div>
                <Link to={`/search?category=${category}`} className="text-sm font-semibold text-[#1A362D] underline-offset-4 hover:underline">
                  See all Perth →
                </Link>
              </div>
              <div className="grid grid-cols-1 gap-px border border-[#E5E4E0] bg-[#E5E4E0] md:grid-cols-2 lg:grid-cols-3">
                {tradies.map((t) => (
                  <Link
                    key={t.id}
                    to={`/tradie/${t.id}`}
                    data-testid={`suburb-tradie-${t.id}`}
                    className="flex flex-col justify-between bg-white p-6 transition-colors hover:bg-[#F3F2EE]"
                  >
                    <div>
                      <div className="flex items-center gap-3">
                        {t.avatar_url ? (
                          <img src={t.avatar_url} alt={t.business_name} className="h-12 w-12 rounded-full object-cover" />
                        ) : (
                          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#1A362D] text-white">
                            {t.business_name?.charAt(0)}
                          </div>
                        )}
                        <div>
                          <div className="font-heading text-lg font-bold text-[#1C1B1A]">{t.business_name}</div>
                          <div className="mt-0.5 flex items-center gap-1 text-xs text-[#8A8988]">
                            <MapPin size={11} /> {t.suburb}
                          </div>
                        </div>
                      </div>
                      <p className="mt-3 line-clamp-2 text-sm text-[#5C5B5A]">{t.bio}</p>
                    </div>
                    <div className="mt-4 border-t border-[#E5E4E0] pt-3">
                      <div className="flex items-center justify-between text-xs">
                        {t.review_count > 0 ? (
                          <div className="flex items-center gap-1 text-[#B08911]"><Star size={12} fill="currentColor" /> {t.avg_rating.toFixed(1)} · {t.review_count} reviews</div>
                        ) : (
                          <div className="text-[#8A8988]">New listing</div>
                        )}
                        {t.phone && (
                          <div className="flex items-center gap-1 text-[#1A362D]"><Phone size={11} /> {t.phone}</div>
                        )}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* SEO body copy — meaningful text for Google */}
      <section className="border-t border-[#E5E4E0] bg-white">
        <div className="mx-auto max-w-4xl px-6 py-12 md:px-10">
          <h2 className="font-heading text-3xl font-bold text-[#1C1B1A]">
            How Real Tradies vets {catName.toLowerCase()}s in {suburbName}
          </h2>
          <div className="mt-6 space-y-4 text-[#5C5B5A]">
            <p>
              Finding a good {catName.toLowerCase()} in {suburbName} shouldn't mean scrolling through paid Google ads
              or being auto-quoted by five strangers who bought your phone number. Real Tradies keeps it simple: every {catName.toLowerCase()}{' '}
              you see below has personally lodged their license, public liability insurance and workers compensation
              paperwork with us. We check the expiry dates in real time and drop them from the list the moment
              anything lapses.
            </p>
            <p>
              You get their direct phone number. You call. They pick up (or they don't — we track answer rates too and
              rank ghosters down). No sales calls from us. No lead-selling to anyone else. It's what a phonebook used
              to feel like, only with modern verification and Perth-first coverage.
            </p>
            <p>
              Are you a {catName.toLowerCase()} in {suburbName}?{' '}
              <Link to="/for-tradies" className="font-semibold text-[#1A362D] underline underline-offset-4">List your trade free forever</Link>. No commissions.
              No paid leads. Ever.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

export { slugify };
